import { useEffect, useState } from 'react';
import { getFundTypeLabel, getWeightTypeLabel } from '../../common/helper';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { getPortfolioList, updatePortfolio } from '../../features/adviceAdmin';
import { fetchPortfolio } from '../../features/global/globalSlice';
import { uipApiInstance } from '../../services';
import { useTranslation } from 'react-i18next';
import { FEATURE_NAMES } from '../../common/constants';
import { checkFFEnabled } from '../../utils';

export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

// API Approval type
export type ApiApproval = {
    _id: string;
    clientId: string;
    country: string;
    created_at: string;
    created_by: string;
    file_path: {
        file_id: string;
        file_name: string;
        file_type: string;
        folder: string;
    };
    is_ft_fullfilled: boolean;
    orgEmail: string;
    portfolioData: {
        config: {
            data: any[];
        };
    };
    portfolio_type: string;
    status: ApprovalStatus;
    tenant_supported_weight_type: string;
    goalCategory?: string;
    portfolioBundleName?: string;
    __meta: {
        action: string;
        author: string;
        comment: string;
        id: string;
        timestamp: string;
        user: string;
    };
};

// UI Approval type for PortfolioApprovalsOrganism
export type Approval = {
    id: string;
    date: string;
    submitter: string;
    userId: string;
    tenant: string;
    property: string;
    fileName: string;
    fileId: string;
    fundTypes: string;
    portfolioType: string;
    status: ApprovalStatus;
    goalCategory?: string;
    portfolioBundleName?: string;
};

// Download handler:
const fileDownload = async (fileId: string, fileName?: string) => {
    try {
        const response = await uipApiInstance.get(`/api/advice/getFile?fileId=${fileId}`, {
            responseType: 'blob'
        });
        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName || `portfolio.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        // Optionally show error to user
        console.error('Failed to download XLSX:', error);
    }
};

export const handleSamplePortfolioDownload = async () => {
    try {
        const metaResponse = await uipApiInstance.get('/api/advice/samplePortfolio');
        const samplePortfolio = metaResponse?.data?.sample_file;

        if (samplePortfolio?.file_id) {
            await fileDownload(samplePortfolio.file_id, samplePortfolio.file_name);
            return;
        }
    } catch (error) {
        // Optionally show error to user
        console.error('Failed to download sample portfolio:', error);
    }
};

export function usePortfolioApprovals() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [apiApprovals, setApiApprovals] = useState<ApiApproval[]>([]);
    const loggedInUsername = useAppSelector((state) => state?.auth?.user?.loggedInUsername);
    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const advisorAdminLoading = useAppSelector((state) => state?.advisorAdmin?.loading);
    const [isLoading, setIsLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState<{ [id: string]: boolean }>({});
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        type: 'success' | 'error' | null;
        icon?: React.ReactNode;
    }>({ open: false, message: '', type: null });

    // get porfolio approvals list
    useEffect(() => {
        setIsLoading(true);
        dispatch(getPortfolioList({ tenant }))
            .then((res: any) => {
                setApiApprovals(res?.payload?.portfolio_list || []);
            })
            .catch((err: any) => {
                setApiApprovals([]);
                console.error('API failed', err);
            })
            .finally(() => setIsLoading(false));
    }, [dispatch, advisorAdminLoading]);

    const approve = async (id: string, to_tenant: string = '') => {
        setActionLoading((prev) => ({ ...prev, [id]: true }));
        setIsLoading(true);
        try {
            let payload: { portfolio_id: string; action: 'APPROVED'; to_tenant?: string } = { portfolio_id: id, action: 'APPROVED' };
            if (to_tenant && checkFFEnabled(FEATURE_NAMES['AEX-2926_ADD_TENANT_PARAMETER_IN_UPDATE_PORTFOLIO'])) {
                payload.to_tenant = to_tenant;
            }
            const result = await dispatch(updatePortfolio(payload));
            if (result.meta && result.meta.requestStatus === 'fulfilled') {
                setApiApprovals((prev) => prev.filter((a) => a._id !== id));
                // refresh market portfolios after approval
                try {
                    await dispatch(fetchPortfolio(tenant));
                } catch (e) {
                    console.error('fetchPortfolio refresh failed while approving pending portfolio', e);
                }
                setSnackbar({
                    open: true,
                    message: t('PORTFOLIO_APPROVED', { userName: loggedInUsername }),
                    type: 'success'
                });
            }
        } finally {
            setActionLoading((prev) => ({ ...prev, [id]: false }));
            setIsLoading(false);
        }
    };

    const reject = async (id: string, to_tenant: string = '') => {
        setActionLoading((prev) => ({ ...prev, [id]: true }));
        setIsLoading(true);
        try {
            let payload: { portfolio_id: string; action: 'REJECTED'; to_tenant?: string } = { portfolio_id: id, action: 'REJECTED' };
            if (to_tenant && checkFFEnabled(FEATURE_NAMES['AEX-2926_ADD_TENANT_PARAMETER_IN_UPDATE_PORTFOLIO'])) {
                payload.to_tenant = to_tenant;
            }
            const result = await dispatch(updatePortfolio(payload));
            if (result.meta && result.meta.requestStatus === 'fulfilled') {
                setApiApprovals((prev) => prev.filter((a) => a._id !== id));
                setSnackbar({
                    open: true,
                    message: t('PORTFOLIO_REJECTED', { userName: loggedInUsername }),
                    type: 'error'
                });
            }
        } finally {
            setActionLoading((prev) => ({ ...prev, [id]: false }));
            setIsLoading(false);
        }
    };

    const handleDownload = fileDownload;

    // The property is fixed now as below
    const propertyType = 'Digital Advisor';

    // Map API approvals to UI approvals
    const mapApiToUi = (a: ApiApproval): Approval => ({
        id: a._id,
        date: a.created_at,
        submitter: a.__meta.author,
        tenant: a.clientId,
        property: propertyType,
        fileName: a.file_path.file_name,
        fileId: a.file_path.file_id,
        fundTypes: getWeightTypeLabel(a.tenant_supported_weight_type),
        portfolioType: getFundTypeLabel(a.portfolio_type),
        status: a.status,
        userId: a.__meta.user,
        goalCategory: a?.goalCategory || '',
        portfolioBundleName: a?.portfolioBundleName || ''
    });

    const pendingApprovals = apiApprovals.filter((a) => a.status === 'PENDING').map(mapApiToUi);

    const handleSnackbarClose = () => setSnackbar((prev) => ({ ...prev, open: false }));
    return {
        pendingApprovals,
        approve,
        reject,
        isLoading,
        actionLoading,
        snackbar,
        handleSnackbarClose,
        handleDownload
    };
}
